/**
 * Team Details Page - View team members and projects, and manage additions
 */
import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Users, Plus, Loader2, FolderKanban, Calendar, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { teamAPI } from '@/api/teamAPI';
import { teamMemberAPI } from '@/api/teamMemberAPI';
import { projectAPI } from '@/api/projectAPI';
import { userAPI } from '@/api/userAPI';
import { Team, TeamMember, Project, User, ProjectMember } from '@/types';
import { AddMembersModal, MemberRole } from '@/components/AddMembersModal';
import { MembersTable } from '@/components/MembersTable';
import { useAppSelector } from '@/store/hooks';
import { projectMemberAPI } from '@/api/projectMemberAPI';

export const TeamDetails = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAppSelector((state) => state.auth);
  const [team, setTeam] = useState<Team | null>(null);
  const [members, setMembers] = useState<ProjectMember[]>([]); // project members for selected project
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]); // all team members (team-level)
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [memberModalOpen, setMemberModalOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [teamMembersLoading, setTeamMembersLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  // const [removingMember, setRemovingMember] = useState<string | null>(null);
  const [removingProject, setRemovingProject] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<{ id: string; email: string } | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [pendingMemberData, setPendingMemberData] = useState<{ userIds: string[]; role: MemberRole } | null>(null);

  const globalRole = currentUser?.role;
  const isTeamAdmin = globalRole === 'admin';

  useEffect(() => {
    if (!teamId) return;

    const loadAll = async () => {
      try {
        setLoading(true);
        const [teamRes, projectsRes] = await Promise.all([
          teamAPI.getTeam(teamId),
          projectAPI.getProjects({ team_id: teamId, is_active: true, limit: 100 }),
        ]);
        setTeam(teamRes);
        setProjects(projectsRes);
        if (projectsRes?.length > 0) {
          // Check if projectId is provided in URL params (from Edit Team button)
          const projectIdFromUrl = searchParams.get('projectId');
          if (projectIdFromUrl && projectsRes.some(p => p.id === projectIdFromUrl)) {
            setSelectedProjectId(projectIdFromUrl);
          } else {
            setSelectedProjectId(projectsRes[0].id);
          }
        }

        // Always load team-level members (used for header count and when no projects)
        try {
          setTeamMembersLoading(true);
          const teamMembersRes = await teamMemberAPI.getTeamMembers(teamId, { skip: 0, limit: 100 });
          setTeamMembers(teamMembersRes);
        } catch (err) {
          console.error('Failed to load team members:', err);
          toast.error('Failed to load team members');
        } finally {
          setTeamMembersLoading(false);
        }
      } catch (error) {
        console.error('Failed to load team details:', error);
        toast.error('Failed to load team details');
      } finally {
        setLoading(false);
      }
    };

    loadAll();
  }, [teamId, searchParams]);

  useEffect(() => {
    if (!teamId || !selectedProjectId) return;
    const loadMembers = async () => {
      try {
        const membersRes = await projectMemberAPI.getProjectMembers(selectedProjectId, true, { skip: 0, limit: 100,membership_type: ["team"] });
        setMembers(membersRes);
      } catch (error) {
        console.error('Failed to load members for project:', error);
        toast.error('Failed to load members for selected project');
        setMembers([]);
      }
    };
    loadMembers();
  }, [projects, teamId, selectedProjectId]);

  const refreshMembers = async () => {
    if (!selectedProjectId) return;
    try {
      setMembersLoading(true);
      const membersRes = await projectMemberAPI.getProjectMembers(selectedProjectId, true, { skip: 0, limit: 100,membership_type: ["team"] });
      setMembers(membersRes);
    } catch (error) {
      console.error('Failed to refresh members:', error);
      toast.error('Failed to refresh members');
    } finally {
      setMembersLoading(false);
    }
  };

  const refreshTeamMembers = async () => {
    if (!teamId) return;
    try {
      setTeamMembersLoading(true);
      const teamMembersRes = await teamMemberAPI.getTeamMembers(teamId, { skip: 0, limit: 100 });
      setTeamMembers(teamMembersRes);
    } catch (error) {
      console.error('Failed to refresh team members:', error);
      toast.error('Failed to refresh team members');
    } finally {
      setTeamMembersLoading(false);
    }
  };

  const refreshProjects = async () => {
    if (!teamId) return;
    try {
      setProjectsLoading(true);
      const projectsRes = await projectAPI.getProjects({ team_id: teamId, is_active: true, limit: 100 });
      setProjects(projectsRes);
    } catch (error) {
      console.error('Failed to refresh projects:', error);
      toast.error('Failed to refresh projects');
    } finally {
      setProjectsLoading(false);
    }
  };

  const openMemberModal = async () => {
    if (!team || !team.organization_id) {
      toast.error('Team organization not found');
      return;
    }
    try {
      setUsersLoading(true);
      setMemberModalOpen(true);

      const allUsers = await userAPI.getUsers({
        organization_id: team.organization_id,
        is_active: true,
        limit: 100,
      });

      // Exclude users already in the team (either via teamMembers or current project members)
      const memberUserIds = new Set([
        ...teamMembers.map((tm) => tm.user_id),
        ...members.map((pm) => pm.user_id),
      ]);
      const filtered = allUsers.filter((u) => !memberUserIds.has(u.id));
      setAvailableUsers(filtered);
    } catch (error) {
      console.error('Failed to load users for team:', error);
      toast.error('Failed to load users for this team');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleAddMembers = async (userIds: string[], role: MemberRole = 'member') => {
    if (!teamId) return;
    if (userIds.length === 0) {
      toast.error('Please select at least one user');
      return;
    }

    try {
      const projectIds = projects.map((p) => p.id).filter(Boolean);

      // If team is linked to multiple projects, show modal to choose OK or Clone
      if (projects.length > 1) {
        const targetProjectId = selectedProjectId || projects[0]?.id;
        if (!targetProjectId) {
          toast.error('No project selected to apply changes');
          return;
        }

        // Store the pending data and show modal
        setPendingMemberData({ userIds, role });
        setShowCloneModal(true);
        setMemberModalOpen(false);
        return;
      }

      // Single project or no projects: directly add members
      await teamMemberAPI.bulkAddMembers({
        team_id: teamId,
        user_ids: userIds,
        role,
        project_ids: projectIds,
        membership_type:"team",
      });

      // Invalidate cache for the affected project(s) so members reload with fresh data
      projectIds.forEach((pid) => {
        projectMemberAPI.invalidateCache(pid);
      });

      toast.success(`Added ${userIds.length} member${userIds.length !== 1 ? 's' : ''} to team projects`);
      setMemberModalOpen(false);
      await refreshMembers();
      await refreshTeamMembers();
    } catch (error: any) {
      if (error?.response?.status === 409) {
        const detail = error.response.data?.detail;
        // console.log("conflict detail ------->", detail);

        if (detail?.has_conflicts && detail?.conflicts?.length > 0) {
          // Build a readable message for each conflicting user
          const conflictMessages = detail.conflicts
            .map((c: any) => `• ${c.username
            } (${c.user_email})`)
            .join('\n');

          toast.error(
            `Cannot assign team. These users are already direct members:\n${conflictMessages}`,
            { duration: 6000 } // show longer so user can read
          );
        } else {
          toast.error(detail?.message || 'Conflict: Team cannot be assigned.');
        }
        return; // stop here, don't close modal
      }
      toast.error(error?.response?.data?.detail || 'Failed to add team members');
    }
  };

  const handleCloneChoice = async (shouldClone: boolean) => {
    if (!teamId || !pendingMemberData) return;

    const { userIds, role } = pendingMemberData;
    const targetProjectId = selectedProjectId || projects[0]?.id;
    
    if (!targetProjectId) {
      toast.error('No project selected');
      setShowCloneModal(false);
      setPendingMemberData(null);
      return;
    }

    try {
      if (shouldClone) {
        // Clone path: create new team for selected project
        const targetProject = projects.find((p) => p.id === targetProjectId);
        if (!team?.organization_id) {
          toast.error('Team organization not found');
          setShowCloneModal(false);
          setPendingMemberData(null);
          return;
        }

        // Get the current project details to find which team it's currently assigned to
        const currentProjectData = await projectAPI.getProject(targetProjectId);
        const currentTeamId = currentProjectData.team_id;

        // Get existing members from the selected project
        const existingMembers = await projectMemberAPI.getProjectMembers(targetProjectId, true, { skip: 0, limit: 100 });
        const existingUserIds = Array.from(new Set([
          ...existingMembers.map((m) => m.user_id),
          ...userIds,
        ]));

        const cloneName = `${team.name} - ${targetProject?.name || 'Clone'}`;
        const newTeam = await teamAPI.createTeam({
          name: cloneName,
          description: team.description,
          organization_id: team.organization_id,
          project_ids: [targetProjectId],
          user_ids: existingUserIds,
        });

        // Remove project from the previous team (if it was assigned to one)
        if (currentTeamId) {
          await teamAPI.removeProjectFromTeam(currentTeamId, targetProjectId);
        }
        
        // Assign the new team to the selected project
        const response =await projectAPI.assignTeamToProject(targetProjectId, newTeam.id);
        
        // Invalidate project member cache so the new team's page shows fresh members
        projectMemberAPI.invalidateCache(targetProjectId);

        toast.success('Member(s) added via cloned team for the selected project');
        
        // Redirect to the newly cloned team page
        navigate(`/teams/${newTeam.id}?projectId=${targetProjectId}`);
      } else {
        // OK path: add to all projects
        const projectIds = projects.map((p) => p.id).filter(Boolean);
        await teamMemberAPI.bulkAddMembers({
          team_id: teamId,
          user_ids: userIds,
          role,
          project_ids: projectIds,
          membership_type:"team"
        });

        // Invalidate cache for all projects to ensure fresh data
        projectIds.forEach(pid => {
          projectMemberAPI.invalidateCache(pid);
        });

        toast.success(`Added ${userIds.length} member${userIds.length !== 1 ? 's' : ''} to all team projects`);
        await refreshMembers();
        await refreshTeamMembers();
      }

      setShowCloneModal(false);
      setPendingMemberData(null);
    } catch (error: any) {
      console.error('Failed to process member addition:', error);
      if (error?.response?.status === 409) {
        const detail = error.response.data?.detail;
        // console.log("conflict detail ------->", detail);

        if (detail?.has_conflicts && detail?.conflicts?.length > 0) {
          const conflictMessages = detail.conflicts
            .map((c: any) => `• ${c.username} (${c.user_email}) (Direct Member) → ${c.project_name}`)
            .join('\n');
        
          toast.error(
            (t) => (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-4">
                  <span className="font-semibold text-sm">Cannot add member — conflict found</span>
                  <button
                    onClick={() => toast.dismiss(t.id)}
                    className="text-white bg-transparent hover:opacity-70 font-bold text-lg leading-none"
                  >
                    ×
                  </button>
                </div>
                <div className="text-sm whitespace-pre-line">{conflictMessages}</div>
              </div>
            ),
            { duration: 4000 } 
          );
        }
        
        else {
          toast.error(detail?.message || 'Conflict: Cannot add member.');
        }

        // ✅ Don't close modal — let user decide what to do
        return;
      }
      toast.error(error?.response?.data?.detail || 'Failed to process member addition');

      setShowCloneModal(false);
      setPendingMemberData(null);
    }
  };

  const handleRemoveMemberAllProjects = async (userId: string) => {
    if (!teamId) return;

    try {
      setIsRemoving(true);
      await teamMemberAPI.removeMemberFromTeamProjects({ team_id: teamId, user_id: userId });

      // Invalidate cache for all projects linked to this team
      const projectIds = projects.map((p) => p.id).filter(Boolean);
      projectIds.forEach((pid) => {
        projectMemberAPI.invalidateCache(pid);
      });

      await refreshMembers();
      await refreshTeamMembers();
      toast.success('Member removed from team and all projects');
    } catch (error: any) {
      console.error('Failed to remove member:', error);
      toast.error(error?.response?.data?.detail || 'Failed to remove member');
    } finally {
      setIsRemoving(false);
    }
  };

  const handleCloneRemoveMemberFromProject = async (userId: string) => {
    if (!teamId || !selectedProjectId || !team) return;
  
    try {
      setIsRemoving(true);
  
      await projectMemberAPI.removeUserFromProject(
        selectedProjectId,
        userId
      );
      // Get existing members of project
      const existingMembers =
        await projectMemberAPI.getProjectMembers(
          selectedProjectId,
          true,
          { skip: 0, limit: 100 }
        );
  
      const remainingUserIds = existingMembers
        .map(m => m.user_id)
        .filter(id => id !== userId);
  
      // Create cloned team WITHOUT removed user
      const cloneName = `${team.name} - Clone`;
      const newTeam = await teamAPI.createTeam({
        name: cloneName,
        description: team.description,
        organization_id: team.organization_id,
        project_ids: [selectedProjectId],
        user_ids: remainingUserIds,
      });
  
      // Remove project from old team
      await teamAPI.removeProjectFromTeam(teamId, selectedProjectId);
  
      // Assign project to new team
      const response =await projectAPI.assignTeamToProject(selectedProjectId, newTeam.id);
  
      // Invalidate caches
      projectMemberAPI.invalidateCache(selectedProjectId);
  
      // IMPORTANT: refresh team members
      await refreshMembers();
  
      toast.success('Member removed via cloned team');
      navigate(`/teams/${newTeam.id}?projectId=${selectedProjectId}`);
    } catch (error: any) {
      console.error('Clone remove failed:', error);
      toast.error(error?.response?.data?.detail || 'Failed to clone & remove member');
    } finally {
      setIsRemoving(false);
    }
  };
  

  const handleUpdateRole = async (
    projectMemberId: string,
    newRole: 'admin' | 'member' | 'viewer'
  ) => {
    if (!isTeamAdmin || !selectedProjectId) {
      toast.error('Only admins can change roles');
      return;
    }

    try {
      await projectMemberAPI.updateMember(projectMemberId, newRole);

      projectMemberAPI.invalidateCache(selectedProjectId);

      toast.success('Member role updated for this project');
      await refreshMembers();
    } catch (error: any) {
      console.error('Failed to update role:', error);
      toast.error(error?.response?.data?.detail || 'Failed to update member role');
    }
  };


  const handleRemoveProject = async (projectId: string, projectName: string) => {
    if (!teamId) return;
    if (!confirm(`Are you sure you want to remove ${projectName} from this team?`)) {
      return;
    }

    try {
      setRemovingProject(projectId);
      await teamAPI.removeProjectFromTeam(teamId, projectId);
      toast.success('Project removed from team');
      await refreshProjects();
    } catch (error: any) {
      console.error('Failed to remove project:', error);
      toast.error(error?.response?.data?.detail || 'Failed to remove project');
    } finally {
      setRemovingProject(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          <p className="mt-2 text-gray-600">Loading team...</p>
        </div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="p-6">
        <p className="text-red-600">Team not found.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Users className="w-8 h-8" />
            {team.name}
          </h1>
          {team.description && (
            <p className="text-sm text-gray-600 mt-1 max-w-2xl">{team.description}</p>
          )}
        </div>
      </div>

      {/* Content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Members Section */}
        <div className="lg:col-span-3 bg-white rounded-lg border border-gray-200 shadow-sm flex flex-col">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Team Members ({teamMembers.length})
              </h2>
              <p className="text-xs text-gray-500 mt-1">People currently in this team</p>
            </div>
            <button
              onClick={openMemberModal}
              className="btn btn-primary btn-sm inline-flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Add Member
            </button>
          </div>
          <div className="flex-1 overflow-y-auto max-h-[60vh]">
            {/* When team has NO projects, show team-level members */}
            {projects.length === 0 ? (
              teamMembersLoading ? (
                <div className="flex items-center justify-center py-8 text-gray-500 gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Loading team members...</span>
                </div>
              ) : teamMembers.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <Users className="w-10 h-10 mb-3" />
                  <p className="font-medium mb-1">No members yet</p>
                  <p className="text-sm mb-3">
                    Add people to start collaborating in this team.
                  </p>
                  <button
                    onClick={openMemberModal}
                    className="btn btn-primary btn-sm inline-flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add First Member
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {teamMembers.map((tm) => (
                    <div
                      key={tm.id}
                      className="flex items-center justify-between px-4 py-3"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-primary-600 flex items-center justify-center text-white text-sm font-medium">
                          {(tm.user_name ||
                            tm.user?.display_name ||
                            tm.user?.username ||
                            tm.user_email ||
                            tm.user?.email ||
                            'U')
                            .charAt(0)
                            .toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {tm.user_name ||
                              tm.user?.display_name ||
                              tm.user?.username ||
                              'User'}
                          </div>
                          {(tm.user_email || tm.user?.email) && (
                            <div className="text-xs text-gray-500">
                              {tm.user_email || tm.user?.email}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800 capitalize">
                          {tm.role}
                        </span>
                        {isTeamAdmin && (
                          <button
                            className="text-red-600 hover:text-red-800"
                            title="Remove from team"
                            onClick={async () => {
                              setMemberToDelete({
                                id: tm.user_id,
                                email: tm.user_email || tm.user?.email || 'this user',
                              });
                              setShowDeleteModal(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : membersLoading ? (
              <div className="flex items-center justify-center py-8 text-gray-500 gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Loading members...</span>
              </div>
            ) : members.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <Users className="w-10 h-10 mb-3" />
                <p className="font-medium mb-1">No members yet</p>
                <p className="text-sm mb-3">
                  Add people to start collaborating in this team.
                </p>
                <button
                  onClick={openMemberModal}
                  className="btn btn-primary btn-sm inline-flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Add First Member
                </button>
              </div>
            ) : (
              <div className="overflow-auto">
                <MembersTable
                  members={members}
                  isAdmin={isTeamAdmin}
                  onRemoveMember={(memberId, email) => {
                    setMemberToDelete({ id: memberId, email });
                    setShowDeleteModal(true);
                  }}
                  onUpdateRole={handleUpdateRole}
                  showActions={true}
                  showJoinedDate={false}
                  showRemoveAction={true}
                  memberShipTypeDetails='team'
                />
              </div>
            )}
          </div>
        </div>

        {/* Projects Section */}
        <div className="lg:col-span-1 bg-white rounded-lg border border-gray-200 shadow-sm flex flex-col">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className='flex-1'>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <FolderKanban className="w-5 h-5" />
                Team Projects ({projects.length})
              </h2>
              <p className="text-xs text-gray-500 mt-1">Projects currently linked to this team</p>
            </div>
            {/* <button
              onClick={refreshProjects}
              className="btn btn-outline btn-xs inline-flex items-center gap-1"
            >
              <Loader2 className="w-3 h-3" />
              Refresh
            </button> */}
          </div>
          <div className="p-4 flex-1 overflow-y-auto max-h-[60vh]">
            {projectsLoading ? (
              <div className="flex items-center justify-center py-8 text-gray-500 gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Loading projects...</span>
              </div>
            ) : projects.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-gray-500">
                <FolderKanban className="w-10 h-10 mb-3" />
                <p className="font-medium mb-1">No projects linked</p>
                <p className="text-xs text-center">
                  Projects created with this team will appear here.
                </p>
              </div>
            ) : (
              <ul className="space-y-3">
                {projects.map((project) => (
                  <li
                    key={project.id}
                    onClick={() => setSelectedProjectId(project.id)}
                    className={`border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50 cursor-pointer ${selectedProjectId === project.id ? 'ring-2 ring-blue-500' : ''}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900">
                          {project.name}{' '}
                          <span className="text-xs text-gray-500">({project.key})</span>
                        </div>
                        {project.description && (
                          <div className="text-xs text-gray-500 line-clamp-2">
                            {project.description}
                          </div>
                        )}
                        {project.start_date && (
                          <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(project.start_date).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Add Members Modal */}
      <AddMembersModal
        isOpen={memberModalOpen}
        title="Add Members to Team"
        description="Select organization users to add as team members."
        availableUsers={availableUsers}
        loadingUsers={usersLoading}
        showRoleSelector={false}
        initialRole="member"
        onClose={() => setMemberModalOpen(false)}
        onConfirm={handleAddMembers}
        confirmLabel="Add Members"
      />


      {showDeleteModal && memberToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Remove Member from Team Projects
            </h3>
            <p className="text-gray-700 mb-6">
              This team is assigned to <strong>{projects.length}</strong> project
              {projects.length !== 1 ? 's' : ''}.<br /><br />
              How would you like to remove
              <span className="font-medium"> {memberToDelete.email} </span>?
            </p>
            <ul className="list-disc list-inside text-sm text-gray-600 mb-6 space-y-2">
              {projects.length == 0 ?
                (
                  <li>
                    <strong>OK:</strong> Remove this member from <strong>team</strong>.
                  </li>
                ) :
                <li>
                  <strong>OK:</strong> Remove this member from <strong>all projects</strong> that belong
                  to this team.
                </li>
              }
              {projects.length > 1 && (
                <li>
                  <strong>Clone:</strong> Remove this member from the <strong>current project only</strong>{' '}
                  by creating a new team for this project and moving the project to that team.
                </li>
              )}
            </ul>

            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300 text-gray-700"
                onClick={() => {
                  setShowDeleteModal(false);
                  setMemberToDelete(null);
                }}
                disabled={isRemoving}
              >
                Cancel
              </button>
              {projects.length > 1 && (
                <button
                  className="px-4 py-2 rounded-md bg-primary-600 hover:bg-primary-700 text-white flex items-center justify-center gap-2"
                  disabled={isRemoving}
                  onClick={async () => {
                    if (!memberToDelete) return;
                    await handleCloneRemoveMemberFromProject(memberToDelete.id);
                    setShowDeleteModal(false);
                    setMemberToDelete(null);
                  }}
                >
                  {isRemoving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Cloning...
                    </>
                  ) : (
                    "Clone"
                  )}
                </button>
              )}
              <button
                className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white flex items-center justify-center gap-2"
                disabled={isRemoving}
                onClick={async () => {
                  if (!memberToDelete) return;
                  await handleRemoveMemberAllProjects(memberToDelete.id);
                  setShowDeleteModal(false);
                  setMemberToDelete(null);
                }}
              >
                {isRemoving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Removing...
                  </>
                ) : (
                  "OK"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clone/OK Modal for multiple projects */}
      {showCloneModal && pendingMemberData && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Team Has Multiple Projects
            </h3>
            <p className="text-gray-700 mb-6">
              This team is assigned to <strong>{projects.length} project{projects.length !== 1 ? 's' : ''}</strong>.
              <br /><br />
              Choose how you want to add the member(s):
            </p>
            <ul className="list-disc list-inside text-sm text-gray-600 mb-6 space-y-2">
              <li><strong>OK:</strong> Add member(s) to <strong>all projects</strong> in this team</li>
              <li><strong>Clone:</strong> Create a new team for the selected project, remove it from this team, and add member(s) to the new team</li>
            </ul>

            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300 text-gray-700"
                onClick={() => {
                  setShowCloneModal(false);
                  setPendingMemberData(null);
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => handleCloneChoice(false)}
              >
                OK
              </button>
              <button
                className="px-4 py-2 rounded-md bg-primary-600 hover:bg-primary-700 text-white"
                onClick={() => handleCloneChoice(true)}
              >
                Clone
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


